import { useState } from 'react';
import styles from './Onboarding.module.css';

const PLUGINS = [
  { id: 'message-center', name: 'Message Center', desc: 'Live chat, tickets, and messaging for your users' },
  { id: 'admin-center', name: 'Admin Center', desc: 'Admin dashboard and user management panel' },
  { id: 'debug-cloud', name: 'Debug Cloud', desc: 'Error tracking and debug reporting' },
];

interface OnboardingProps {
  email: string;
  onComplete: (data: { name: string; company?: string; plugins: string[] }) => Promise<unknown>;
}

export function Onboarding({ email, onComplete }: OnboardingProps) {
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [selectedPlugins, setSelectedPlugins] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const togglePlugin = (id: string) => {
    setSelectedPlugins((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || selectedPlugins.size === 0) return;

    setSubmitting(true);
    setError('');
    try {
      await onComplete({
        name: name.trim(),
        company: company.trim() || undefined,
        plugins: [...selectedPlugins],
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Signup failed');
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <h1 className={styles.title}>Welcome to ChikChak Labs</h1>
        <p className={styles.subtitle}>
          Let's set up your account. You'll get a free 14-day trial key for each plugin.
        </p>

        <form onSubmit={handleSubmit}>
          <div className={styles.field}>
            <label className={styles.label}>Email</label>
            <input className={styles.input} type="text" value={email} disabled />
          </div>

          <div className={styles.field}>
            <label className={styles.label}>Your Name *</label>
            <input
              className={styles.input}
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Jane Smith"
              required
            />
          </div>

          <div className={styles.field}>
            <label className={styles.label}>Company</label>
            <input
              className={styles.input}
              type="text"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              placeholder="Acme Inc. (optional)"
            />
          </div>

          <div className={styles.field}>
            <label className={styles.label}>Choose Plugins *</label>
            <p className={styles.pluginHint}>Select one or more. Each gets its own 14-day trial key.</p>
            <div className={styles.pluginGrid}>
              {PLUGINS.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  className={`${styles.pluginCard} ${selectedPlugins.has(p.id) ? styles.pluginCardSelected : ''}`}
                  onClick={() => togglePlugin(p.id)}
                >
                  <div className={styles.pluginName}>{p.name}</div>
                  <div className={styles.pluginDesc}>{p.desc}</div>
                </button>
              ))}
            </div>
          </div>

          {error && <div className={styles.error}>{error}</div>}

          <button
            type="submit"
            className={styles.submitBtn}
            disabled={!name.trim() || selectedPlugins.size === 0 || submitting}
          >
            {submitting ? 'Setting up...' : `Get Started — Free Trial${selectedPlugins.size > 1 ? ` (${selectedPlugins.size} plugins)` : ''}`}
          </button>
        </form>
      </div>
    </div>
  );
}
